import {
    Box,
    Button,
    Heading,
    HStack,
    PinInput,
    PinInputField,
    Text,
    useBoolean,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout, Logo } from 'components/atoms'
import { InputBuild } from 'components/molecules'
import { regexForm } from 'components/utils/regexForm'
import { validatorsForm } from 'components/utils/validatorsForm'
import { useAuthContext } from 'contexts/auth'
import { AuthRequestResetPassword, ErrorResponseDTO } from 'models'
import React, { KeyboardEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import PasswordListError from '../components/PasswordListError'
import { errorsAuth } from '../utils/errorsAuth'
import { InputFormReset } from './form'
import { FormResetElementsValues } from './interface'
import { InputFormResetElements } from './types'

const Reset: React.FC = () => {
    const toast = useToast()
    const { reset } = useAuthContext()
    const navigate = useNavigate()
    const [code, setCode] = useState(false)

    const schema = yup
        .object({
            codigoConfirmacao: yup
                .string()
                .length(6)
                .required(validatorsForm.codigoConfirmacao.required),
            nomeUsuario: yup.string().required(),
            senha: yup
                .string()
                .min(8, 'Senha deve ter pelo menos 8 caracteres')
                .max(30, 'Senha deve ter no máximo 30 caracteres')
                .matches(regexForm.senha)
                .required(),
            senhaConfirmacao: yup
                .string()
                .oneOf([yup.ref('senha'), null], 'Senhas não coincidem.')
                .required()
        })
        .required()

    const {
        handleSubmit,
        register,
        watch,
        formState: { errors, isSubmitting },
        setValue
    } = useForm<FormResetElementsValues>({
        resolver: yupResolver(schema)
    })

    const all = watch()

    const [searchView, setSearchView] = useBoolean()

    const onSubmit = async (values: AuthRequestResetPassword) => {
        return reset(values)
            .then(response => {
                navigate('/entrar/')
            })
            .catch(({ response }) => {
                const { data } = response as ErrorResponseDTO
                data.filter(
                    (elem, index, arr) =>
                        index ===
                        arr.findIndex(
                            checkEle => checkEle.codigo === elem.codigo
                        )
                ).map(res => {
                    const error = errorsAuth[res.codigo]
                    toast({
                        title: error.title,
                        description: error.description,
                        position: 'bottom-left',
                        status: 'error',
                        isClosable: true,
                        variant: 'solid-error'
                    })
                })
            })
    }

    const handleCode = () => {
        watch('codigoConfirmacao')?.length === 6 && setCode(true)
    }

    const handleEnter = (event: KeyboardEvent<HTMLInputElement>) => {
        ;(event.key === 'Enter' || event.keyCode === 13) && handleCode()
    }

    useEffect(() => {
        setCode(watch('codigoConfirmacao')?.length === 6)
    }, [])

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            h="100vh"
            w="100%"
        >
            <Box display="grid" w="100%" maxW="2xl" gap={8}>
                <Box>
                    <Logo maxW={187} />
                </Box>
                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading size="md" textAlign="center" mb="2">
                        {'Recuperação de senha'}
                    </Heading>
                    <Text textAlign="center">
                        {code
                            ? 'Preencha os campos a seguir para finalizar o seu cadastro'
                            : 'Confirme o código de segurança enviado por e-mail.'}
                    </Text>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Layout type="container" full px={6} mt={8}>
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {code ? (
                                    <>
                                        {InputFormReset.map(
                                            ({ layout, input }, key) => (
                                                <Layout {...layout} key={key}>
                                                    <InputBuild
                                                        {...input}
                                                        err={
                                                            errors[
                                                                input.id as InputFormResetElements
                                                            ]
                                                        }
                                                        {...register(
                                                            input.id as InputFormResetElements
                                                        )}
                                                        value={
                                                            watch(
                                                                input.id as InputFormResetElements
                                                            ) as string
                                                        }
                                                    ></InputBuild>
                                                </Layout>
                                            )
                                        )}
                                        <Layout
                                            type="col"
                                            layout={{ base: 12 }}
                                        >
                                            <PasswordListError
                                                password={all.senha}
                                            />
                                        </Layout>
                                        <Layout
                                            type="col"
                                            layout={{ base: 12, md: 6 }}
                                        >
                                            <Button
                                                size="md"
                                                w="full"
                                                colorScheme="primary"
                                                data-testid="btn-submit-cancel"
                                                variant="outline"
                                                onClick={() => setCode(false)}
                                            >
                                                Voltar
                                            </Button>
                                        </Layout>
                                        <Layout
                                            type="col"
                                            layout={{ base: 12, md: 6 }}
                                        >
                                            <Button
                                                size="md"
                                                w="full"
                                                colorScheme="primary"
                                                data-testid="btn-submit-finish"
                                                type="submit"
                                                isLoading={isSubmitting}
                                            >
                                                Finalizar
                                            </Button>
                                        </Layout>
                                    </>
                                ) : (
                                    <>
                                        <Layout
                                            type="col"
                                            layout={{ base: 12 }}
                                            textAlign="center"
                                        >
                                            <InputBuild
                                                id="codigoConfirmacao"
                                                label="Código de segurança"
                                                required
                                                hidden
                                                inputMode="numeric"
                                                err={
                                                    errors['codigoConfirmacao']
                                                }
                                                {...register(
                                                    'codigoConfirmacao'
                                                )}
                                                value={
                                                    watch(
                                                        'codigoConfirmacao'
                                                    ) as string
                                                }
                                            />
                                            <HStack
                                                justifyContent="center"
                                                spacing="16px"
                                            >
                                                <PinInput
                                                    placeholder="_"
                                                    onChange={value =>
                                                        setValue(
                                                            'codigoConfirmacao',
                                                            value
                                                        )
                                                    }
                                                    defaultValue=""
                                                >
                                                    {Array.from({
                                                        length: 6
                                                    }).map((_, i) => (
                                                        <PinInputField
                                                            key={i}
                                                            data-testid={`Pin_${i}`}
                                                            onKeyUp={e =>
                                                                handleEnter(e)
                                                            }
                                                        />
                                                    ))}
                                                </PinInput>
                                            </HStack>
                                        </Layout>
                                        <Layout
                                            type="col"
                                            layout={{ base: 12 }}
                                            textAlign="center"
                                        >
                                            <Button
                                                w="full"
                                                maxW="320px"
                                                colorScheme="primary"
                                                size="md"
                                                onClick={handleCode}
                                                data-testid="btn-submit"
                                                disabled={
                                                    watch('codigoConfirmacao')
                                                        ?.length !== 6
                                                }
                                            >
                                                Confirmar código
                                            </Button>
                                        </Layout>
                                    </>
                                )}
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Box>
    )
}

export default Reset
